import { useCallback, useMemo } from "react";
import { API_URL } from "constants/index";
import { toast } from "react-toastify";
import { useLazyLoading } from "shared/components/UniversalTable";

/**
 * Filter parameters for building financials data
 */
export interface BuildingFinancialsFilters {
  searchText?: string;
  companyIds?: string[];
}

/**
 * Custom hook for fetching and managing building financials data
 * Uses lazy loading for optimal performance with large datasets
 *
 * @param filters - Server-side filter parameters
 */
export const useBuildingFinancialsData = (filters: BuildingFinancialsFilters = {}) => {
  // Build additional params from filters
  const additionalParams = useMemo(() => {
    const params: Record<string, string> = {};

    if (filters.searchText) {
      params.searchText = filters.searchText;
    }

    if (filters.companyIds && filters.companyIds.length > 0) {
      params.companyIds = filters.companyIds.join(',');
    }

    return params;
  }, [
    filters.searchText,
    filters.companyIds?.join(',')
  ]);

  // Lazy loading for all building financials (using simplified endpoint with caching)
  // PERFORMANCE: Initial page size of 20 for faster first paint (800ms-2s vs 2-5s)
  // Subsequent loads use 50 buildings per page
  const {
    data: buildingFinancials,
    isLoading,
    isLoadingMore,
    hasMore,
    totalCount,
    observerTarget,
    setData: setBuildingFinancials,
    fetch: fetchBuildingFinancials
  } = useLazyLoading<any>({
    endpoint: `${API_URL}/building-financials/all-buildings-simplified`,
    pageSize: 20, // Changed from 50 to 20 for faster initial load
    sortField: 'id',
    sortDirection: 'ASC',
    additionalParams,
    onFetchSuccess: (data) => {
      // DEBUG: Log first building's data to see what API returns
      if (data && data.length > 0) {
        const firstBuilding = data[0];
        console.log('🔍 API Response - First building data:', {
          id: firstBuilding.id,
          actualIndexedContractualRentsResidential: firstBuilding.actualIndexedContractualRentsResidential,
          actualResidentialRentInTerminationPhase: firstBuilding.actualResidentialRentInTerminationPhase,
          actualIndexedContractualRentsRetail: firstBuilding.actualIndexedContractualRentsRetail,
          actualRetailRentInTerminationPhase: firstBuilding.actualRetailRentInTerminationPhase,
          actualIndexedContractualRentsOffices: firstBuilding.actualIndexedContractualRentsOffices,
          actualOfficeRentInTerminationPhase: firstBuilding.actualOfficeRentInTerminationPhase,
          actualIndexedContractualRentsTotal: firstBuilding.actualIndexedContractualRentsTotal,
          actualTotalRentInTerminationPhase: firstBuilding.actualTotalRentInTerminationPhase,
          ervOnVacantUnits: firstBuilding.ervOnVacantUnits,
          actualVacantUnits: firstBuilding.actualVacantUnits,
          potentialGrossIncomeActual: firstBuilding.potentialGrossIncomeActual,
          deltaRentalValueSinceAcquisition: firstBuilding.deltaRentalValueSinceAcquisition,
        });
        console.log('🔍 Full first building object:', firstBuilding);
      }
    },
    onFetchError: (error) => {
      console.error("Error fetching building financials:", error);
      toast.error("Fout bij ophalen van financiële gebouwgegevens");
    }
  });

  // Refresh building financials (force reload)
  const refreshBuildingFinancials = useCallback(async () => {
    await fetchBuildingFinancials(false); // Reset mode
  }, [fetchBuildingFinancials]);

  // Manual load more (fallback if lazy loading fails)
  const loadMoreManually = useCallback(async () => {
    await fetchBuildingFinancials(true); // Append mode
  }, [fetchBuildingFinancials]);

  return {
    buildingFinancials,
    isLoading,
    isLoadingMore,
    hasMore,
    totalCount,
    observerTarget,
    setBuildingFinancials,
    refreshBuildingFinancials,
    loadMoreManually
  };
};
